export const AGENT_SPEC_API_CONTRACT = "agent-authoring/v2" as const;
export const AGENT_SPEC_SCHEMA = "agentmaurice.agent_spec/v2" as const;
export const AGENT_SPEC_CONTRACT_BUNDLE_SHA256 =
  "a8cb6603d2498905350f160acf4d74c6d54a8b68fc397bf2550ee121cd8a1b98" as const;

export type AgentSpecComponentKind = "workflow" | "miniapp";
export type ManagementMode = "managed" | "unmanaged";
export type AgentSpecPlanStatus =
  | "prepared"
  | "approved"
  | "applying"
  | "applied"
  | "failed"
  | "expired"
  | "superseded";
export type AgentSpecRiskLevel = "low" | "medium" | "high" | "critical";

export interface AgentSpecIdentity {
  name: string;
  description: string;
  instructions: string;
}

export interface AgentSpecMCPRequirement {
  registry_name: string;
  runtime_provider?: "docker";
  required_tools?: string[];
}

export interface AgentSpecCapability {
  name: string;
  version: string;
  description: string;
  workflow_id: string;
  allowed_agent_ids?: string[];
}

export interface AgentSpecDocumentV2 {
  $schema: typeof AGENT_SPEC_SCHEMA;
  schema_version: 2;
  agent_id: string;
  agent_alias: string;
  agent_spec_id: string;
  identity: AgentSpecIdentity;
  capabilities?: AgentSpecCapability[];
  dependencies?: Array<Record<string, unknown>>;
  mcp_requirements?: AgentSpecMCPRequirement[];
  parameters?: Record<string, unknown>;
  secret_references?: Record<string, string>;
  policy_references?: string[];
}

export interface AgentSpecProjectFile {
  path: string;
  content: string;
}

export interface AgentSpecSourceControl {
  commit_sha: string;
  branch: string;
  repository_url: string;
  pull_request_url?: string;
  workspace_hash?: string;
  dirty?: boolean;
}

export interface AgentSpecGovernance {
  profile:
    | "sandbox"
    | "development"
    | "integration_test"
    | "staging"
    | "production"
    | "unknown";
  environment_kind: string;
  approval_mode: "none" | "human" | "distinct_human";
  require_clean_git: boolean;
  require_commit_sha: boolean;
  require_pull_request_url: boolean;
  require_studio_revision: boolean;
  authoring_channel: "code_agent" | "studio_human";
  review_mechanism: "none" | "pull_request" | "studio_revision";
  tests: "auto" | "on" | "off";
}

export interface AgentSpecComponent {
  component_id: string;
  kind: AgentSpecComponentKind;
  title: string;
  version: string;
  management_mode: ManagementMode;
  owner_agent_spec_id?: string;
  resource_version?: string;
  content_hash?: string;
  file_path: string;
}

export interface AgentSpecDiagnostic {
  severity: "error" | "warning" | "info" | string;
  code: string;
  message: string;
  path?: string;
  json_pointer?: string;
  keyword?: string;
  expected?: unknown;
  actual?: unknown;
  suggestion?: string;
}

export interface AgentSpecInitResult {
  schema_version: "agentmaurice.agent_spec_init/v2";
  status: "created" | "existing";
  organization_id: string;
  agent_id: string;
  agent_alias: string;
  agent_spec_id: string;
  resource_version: number;
  next_command: string;
}

export interface AgentSpecPullResult {
  schema_version: "agentmaurice.agent_spec_pull/v2";
  organization_id: string;
  agent_id: string;
  agent_alias: string;
  agent_spec_id: string;
  resource_version: number;
  spec_hash: string;
  files: AgentSpecProjectFile[];
  source_control?: Partial<AgentSpecSourceControl>;
  observed_state?: Record<string, unknown>;
  governance?: AgentSpecGovernance;
  contract: {
    version: string;
    bundle_sha256: string;
  };
  warnings: AgentSpecDiagnostic[];
}

export interface AgentSpecCheckResult {
  schema_version: "agentmaurice.agent_spec_check/v2";
  ok: boolean;
  complete: boolean;
  organization_id: string;
  agent_id: string;
  agent_alias: string;
  agent_spec_id: string;
  base_resource_version: number;
  spec_hash?: string;
  diagnostics: AgentSpecDiagnostic[];
  summary: {
    workflows_and_miniapps?: number;
    has_test_plan?: boolean;
    contract_bundle_sha256?: string;
    [key: string]: unknown;
  };
  governance?: AgentSpecGovernance;
}

export interface AgentSpecDiffChange {
  component: string;
  key?: string;
  action: "added" | "removed" | "modified" | string;
  old_value?: unknown;
  new_value?: unknown;
}

export interface AgentSpecSemanticDiff {
  from_version: number;
  to_version: number;
  content_diff: string;
  spec_changes: AgentSpecDiffChange[];
  summary: string;
  stats: {
    lines_added: number;
    lines_removed: number;
    spec_added: number;
    spec_removed: number;
    spec_modified: number;
  };
}

export interface AgentSpecTestPolicy {
  mode?: "auto" | "on" | "off";
  environment_kind?: "development" | "integration_test" | "production" | string;
  has_test_plan?: boolean;
  baseline_checks_blocking?: boolean;
  test_plan_blocking?: boolean;
  skip_allowed?: boolean;
  reason?: string;
  [key: string]: unknown;
}

export interface AgentSpecPlan {
  schema_version: "agentmaurice.agent_spec_plan/v2";
  organization_id: string;
  agent_id: string;
  agent_alias: string;
  agent_spec_id: string;
  plan_id: string;
  plan_hash: string;
  spec_hash: string;
  base_resource_version: number;
  status: AgentSpecPlanStatus;
  risk: AgentSpecRiskLevel;
  blast_radius: string[];
  diff?: AgentSpecSemanticDiff;
  fingerprints: Record<string, string>;
  component_versions: Record<string, string>;
  test_policy: AgentSpecTestPolicy;
  source_control: Partial<AgentSpecSourceControl>;
  expires_at: string;
  next_action: string;
  approval_id?: string;
  governance?: AgentSpecGovernance;
}

export interface AgentSpecPlanSummary {
  schema_version: "agentmaurice.agent_spec_plan/v2";
  organization_id: string;
  agent_id: string;
  agent_spec_id: string;
  plan_id: string;
  plan_hash: string;
  spec_hash: string;
  base_resource_version: number;
  status: AgentSpecPlanStatus;
  risk: AgentSpecRiskLevel;
  blast_radius: string[];
  diff?: AgentSpecSemanticDiff;
  fingerprints: Record<string, string>;
  component_versions: Record<string, string>;
  test_policy: AgentSpecTestPolicy;
  git_commit: string;
  created_by?: string;
  approval_id?: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  applied_at?: string;
  next_action: string;
  governance?: AgentSpecGovernance;
  failure?: Record<string, unknown>;
}

export interface AgentSpecExecutableArtifact {
  schema_version: "agentmaurice.agent_spec_executable_artifact/v2";
  plan_hash: string;
  organization_id: string;
  agent_id: string;
  agent_alias: string;
  environment_name: string;
  environment_kind: string;
  agent_spec_id: string;
  base_resource_version: number;
  spec_hash: string;
  files: AgentSpecProjectFile[];
  compiled_ir_sha256: string;
  fingerprints: Record<string, string>;
  component_versions: Record<string, string>;
  touched_components: string[];
  dependency_preconditions: Record<string, unknown>;
  test_policy: AgentSpecTestPolicy;
  ownership_operations: Array<Record<string, unknown>>;
  source_control: Partial<AgentSpecSourceControl>;
}

export interface AgentSpecPlanDetail extends AgentSpecPlanSummary {
  executable_artifact: AgentSpecExecutableArtifact;
  verification?: AgentSpecVerification;
  current_runtime?: {
    plan_id?: string;
    plan_hash?: string;
    spec_hash?: string;
    applied_at?: string;
    from_another_plan?: boolean;
  };
}

export interface AgentSpecPlanListResult {
  schema_version: "agentmaurice.agent_spec_plan/v2";
  organization_id: string;
  agent_id: string;
  items: AgentSpecPlanSummary[];
  count: number;
}

export interface AgentSpecApproval {
  schema_version: "agentmaurice.agent_spec_approval/v2";
  plan_id: string;
  plan_hash: string;
  approval_id: string;
  status: "approved" | "expired" | "consumed" | "revoked";
  approved_by: string;
  expires_at: string;
  approval_kind?: "human" | "policy" | "internal";
  approval_mode?: "none" | "human" | "distinct_human";
  principal_type?: string;
}

export interface AgentSpecApplyResult {
  schema_version: "agentmaurice.agent_spec_apply/v2";
  plan_id: string;
  plan_hash: string;
  approval_id: string;
  status: "applying" | "applied" | "failed";
  replayed: boolean;
  spec_hash: string;
  apply?: Record<string, unknown>;
  applied_at?: string;
  evidence?: Record<string, unknown>;
  failure?: Record<string, unknown>;
}

export interface AgentSpecVerification {
  schema_version: "agentmaurice.agent_spec_verify/v2";
  plan_id: string;
  plan_hash: string;
  status: AgentSpecPlanStatus;
  verified: boolean;
  checks: {
    plan_applied?: boolean;
    approval_applied?: boolean;
    desired_state_match?: boolean;
    drift?: boolean;
    provenance_commit?: string;
    [key: string]: unknown;
  };
  diagnostics: AgentSpecDiagnostic[];
}

export interface InitAgentSpecInput {
  agent_alias: string;
  title?: string;
}

export interface CheckAgentSpecInput {
  agent_alias: string;
  environment_name?: string;
  files: AgentSpecProjectFile[];
  base_resource_version?: number;
  source_control: AgentSpecSourceControl;
}

export type PrepareAgentSpecPlanInput = CheckAgentSpecInput;

export interface ApproveAgentSpecPlanInput {
  approval_text?: string;
  comment?: string;
}

export interface ApplyAgentSpecPlanInput {
  plan_hash: string;
  approval_id: string;
  tests?: "auto" | "on" | "off";
  skip_reason?: string;
  idempotency_key: string;
}

export interface AgentSpecAPIErrorBody {
  $schema?: "agentmaurice.cli_error/v2";
  schema_version?: 2;
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface AgentSpecAPIEnvelope<T> {
  data: T;
}

export type AgentSpecStudioThreadStatus = "active" | "archived";
export type AgentSpecStudioRevisionStatus =
  "draft" | "sealed" | "planned" | "terminal";

export interface AgentSpecStudioGoal {
  id: string;
  title: string;
  description?: string;
  success_criteria?: string;
  source: "api" | "ticket";
}

export type AgentSpecStudioJobStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "blocked"
  | "cancelled";

export interface AgentSpecStudioLoopContract {
  loop_type: "closed_loop" | string;
  iteration_policy: { max_iterations: number };
  verification_gates: Array<{ id: string; type: string }>;
  failure_policy: { on_repeated_gate_failure: string };
}

export interface AgentSpecStudioJobState {
  job_id: string;
  status: AgentSpecStudioJobStatus;
  thread_id: string;
  goal_id?: string;
  loop_contract: AgentSpecStudioLoopContract;
  closeout?: AgentSpecStudioCloseoutState;
  updated_at?: string;
}

export interface AgentSpecStudioThreadSummary {
  id: string;
  organization_id: string;
  agent_id: string;
  environment_name: string;
  title: string;
  status: AgentSpecStudioThreadStatus;
  active_cycle: number;
  initiated_by?: string;
  active_revision_id?: string;
  next_event_sequence: number;
  application_key?: string;
  last_line?: string;
  attention?: "needs_approval" | "blocked" | "running" | string;
  goal?: AgentSpecStudioGoal;
  created_at: string;
  updated_at: string;
}

export interface AgentSpecStudioMessage {
  id: string;
  sequence: number;
  role: "user" | "assistant" | "system";
  author_id?: string;
  content: string;
  attachment_ids?: string[];
  action_hints?: AgentSpecStudioActionHint[];
  artifacts?: AgentSpecStudioArtifact[];
  created_at: string;
}

export type AgentSpecStudioArtifactKind =
  | "runtime_tool_result"
  | "workflow_execution"
  | "staff_dispatch"
  | "execution_plan";

export interface AgentSpecStudioArtifact {
  kind: AgentSpecStudioArtifactKind;
  tool_name?: string;
  workflow_id?: string;
  execution_id?: string;
  mode?: "sync" | "async" | "preview";
  status?: string;
  duration_ms?: number;
  trace_id?: string;
  result?: unknown;
  error?: string;
  truncated?: boolean;
}

// Payload de l'artefact `execution_plan` (chatserver : `result`, forme
// AgentSpecStudioExecutionPlan). Le graphe est proposé ; seul un geste humain le lance.
export interface AgentSpecExecutionPlanPhase {
  phase_key: string;
  name: string;
  node_count: number;
}

export interface AgentSpecExecutionPlanBudget {
  max_parallel_jobs?: number;
  max_total_jobs?: number;
  max_total_attempts?: number;
  timeout_seconds?: number;
  max_cost_minor_units?: number;
  currency?: string;
}

export interface AgentSpecExecutionPlan {
  graph_id: string;
  revision: number;
  goal: string;
  status: string;
  node_count: number;
  phases: AgentSpecExecutionPlanPhase[];
  budget_policy: AgentSpecExecutionPlanBudget;
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

// Parseur tolérant : un champ mal formé est ignoré, un artefact sans
// graph_id ou sans révision entière ne produit pas de carte.
export function executionPlanFromArtifact(
  artifact: AgentSpecStudioArtifact | null | undefined,
): AgentSpecExecutionPlan | null {
  if (!artifact || artifact.kind !== "execution_plan") return null;
  const raw = artifact.result;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  const graphId = typeof value.graph_id === "string" ? value.graph_id.trim() : "";
  const revision = finiteNumber(value.revision);
  if (!graphId || revision === undefined || !Number.isInteger(revision)) return null;

  const phases: AgentSpecExecutionPlanPhase[] = Array.isArray(value.phases)
    ? value.phases.flatMap((item): AgentSpecExecutionPlanPhase[] => {
        if (!item || typeof item !== "object") return [];
        const phase = item as Record<string, unknown>;
        if (typeof phase.phase_key !== "string" || !phase.phase_key) return [];
        return [
          {
            phase_key: phase.phase_key,
            name: typeof phase.name === "string" && phase.name ? phase.name : phase.phase_key,
            node_count: finiteNumber(phase.node_count) ?? 0,
          },
        ];
      })
    : [];

  const budgetRaw =
    value.budget_policy && typeof value.budget_policy === "object"
      ? (value.budget_policy as Record<string, unknown>)
      : {};
  const budget: AgentSpecExecutionPlanBudget = {
    max_parallel_jobs: finiteNumber(budgetRaw.max_parallel_jobs),
    max_total_jobs: finiteNumber(budgetRaw.max_total_jobs),
    max_total_attempts: finiteNumber(budgetRaw.max_total_attempts),
    timeout_seconds: finiteNumber(budgetRaw.timeout_seconds),
    max_cost_minor_units: finiteNumber(budgetRaw.max_cost_minor_units),
    currency: typeof budgetRaw.currency === "string" ? budgetRaw.currency : undefined,
  };

  return {
    graph_id: graphId,
    revision,
    goal: typeof value.goal === "string" ? value.goal : "",
    status: typeof value.status === "string" ? value.status : "",
    node_count:
      finiteNumber(value.node_count) ??
      phases.reduce((total, phase) => total + phase.node_count, 0),
    phases,
    budget_policy: budget,
  };
}

export type AgentSpecStudioActionKind =
  | "prepare_plan"
  | "approve"
  | "approve_architecture"
  | "connect_integration"
  | "confirm_integration"
  | "choose_provider"
  | "blocked"
  | "apply"
  | "verify"
  | "preview"
  | "test"
  | "create_sandbox_agent"
  | "new_revision"
  | "provide_runtime_binding";

export interface AgentSpecStudioActionHint {
  kind: AgentSpecStudioActionKind;
  resource_id?: string;
  plan_id?: string;
  payload?: Record<string, unknown>;
}

export interface AgentSpecStudioRevision {
  id: string;
  thread_id: string;
  cycle: number;
  sequence: number;
  status: AgentSpecStudioRevisionStatus;
  parent_revision_id?: string;
  base_resource_version: number;
  initiated_by: string;
  contributors: string[];
  files: AgentSpecProjectFile[];
  diagnostics: AgentSpecDiagnostic[];
  workspace_hash?: string;
  revision_hash?: string;
  commit_sha?: string;
  signature?: string;
  plan_id?: string;
  attestation_valid: boolean;
  sealed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentSpecStudioAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  status: "uploaded" | "scanning" | "ready" | "rejected";
  rejection_reason?: string;
  created_at: string;
}

export type AgentSpecStudioCloseoutStatus =
  | ""
  | "awaiting_approval"
  | "closing_out"
  | "succeeded"
  | "repairing"
  | "blocked";

export interface AgentSpecStudioCloseoutState {
  status: AgentSpecStudioCloseoutStatus;
  plan_id?: string;
  plan_hash?: string;
  repair_cycles: number;
  max_repair_cycles: number;
  evidence?: Record<string, unknown>;
  updated_at?: string;
}

export interface AgentSpecStudioCloseoutResult {
  schema_version: "agentmaurice.agent_spec_studio/v2";
  thread: AgentSpecStudioThreadSummary;
  closeout: AgentSpecStudioCloseoutState;
  revision?: AgentSpecStudioRevision;
  apply?: AgentSpecApplyResult;
  verify?: AgentSpecVerification;
  plan?: AgentSpecPlan;
}

export interface AgentSpecStudioThread {
  schema_version: "agentmaurice.agent_spec_studio/v2";
  thread: AgentSpecStudioThreadSummary;
  messages: AgentSpecStudioMessage[];
  revision?: AgentSpecStudioRevision;
  attachments: AgentSpecStudioAttachment[];
  closeout?: AgentSpecStudioCloseoutState;
  job?: AgentSpecStudioJobState;
  goal?: AgentSpecStudioGoal;
}

export interface AgentSpecStudioCreateJobFromGoalResult {
  schema_version: "agentmaurice.agent_spec_studio/v2";
  thread: AgentSpecStudioThreadSummary;
  job: AgentSpecStudioJobState;
  goal: AgentSpecStudioGoal;
}

export interface AgentSpecStudioThreadList {
  schema_version: "agentmaurice.agent_spec_studio/v2";
  threads: AgentSpecStudioThreadSummary[];
}

export interface AgentSpecStudioRealtimeSession {
  session: { id: string; status: "active" };
  space: { id: string; name: string; description?: string };
  deployment_id: string;
  target_deployment_id: string;
  token: string;
  url: string;
}

export interface AgentSpecStudioPrepareResult {
  schema_version: "agentmaurice.agent_spec_studio/v2";
  revision: AgentSpecStudioRevision;
  check: AgentSpecCheckResult;
  plan: AgentSpecPlan;
  /** Present when sandbox/dev policy auto-closeouts after prepare. */
  closeout?: AgentSpecStudioCloseoutState;
}

export interface AgentSpecStudioEvent {
  sequence: number;
  type: string;
  payload: Record<string, unknown>;
}

export interface AgentSpecStudioWorkflowPreview {
  execution_id: string;
  workflow_id: string;
  state: string;
  expires_at: string;
}

export interface AgentSpecStudioMiniAppPreview {
  preview_id: string;
  expires_at: string;
  runtime: {
    app_instance_id: string;
    miniapp_id: string;
    status: string;
    state: Record<string, unknown>;
    state_version: number;
    ui?: {
      view: string;
      tree: Record<string, unknown>;
      format?: string;
      library?: string;
      program?: string;
      initial_state?: Record<string, unknown>;
      fallback_tree?: Record<string, unknown>;
    };
    forms?: Record<string, unknown>;
    action_bindings?: Record<string, { event_id: string }>;
    effects?: Array<{ type: string; payload?: Record<string, unknown> }>;
  };
}
